import React from "react";

import Text from "@afa-shared/afa-components/dist/Text";
import { StandardLink } from "@components/StandardLink";
import { ExtendedEventData } from "@containers/EventPage";
import { getInternalOrExternalIcon } from "@utils/linkIconHelper";

import eventPageStyles from "./EventPage.module.css";

type RegistrationButtonProps = {
  type: "physical" | "remote";
  eventPage: ExtendedEventData;
};
const RegistrationButton = ({ type, eventPage }: RegistrationButtonProps) => {
  const url =
    type === "remote" ? eventPage?.registrationLinkTwo?.url : eventPage?.registrationLink?.url;
  const linkText =
    type === "remote"
      ? eventPage?.registrationLinkTwo?.linkText
      : eventPage?.registrationLink?.linkText;

  const newTab =
    type === "remote"
      ? eventPage?.registrationLinkTwo?.newTab
      : eventPage?.registrationLink?.newTab;

  return (
    url && (
      <StandardLink
        type={getInternalOrExternalIcon(url, null, null)}
        text={linkText}
        url={url}
        variant={"button"}
        hideIcon={url ? false : true}
        target={newTab ? "_blank" : "_self"}
        contentfulEntryId={eventPage.sys.id}
        contentfulFieldId={type === "remote" ? "registrationLinkTwo" : "registrationLink"}
      />
    )
  );
};

export const RegistrationButtonSection = (eventPage: ExtendedEventData) => {
  const showPhysicalRegistrationButton =
    eventPage?.physicalRegistrationIsOpen && eventPage?.registrationLink?.url !== null;
  const showRemoteRegistrationButton =
    eventPage?.remoteRegistrationIsOpen && eventPage?.registrationLinkTwo?.url !== null;
  const showBothButtons = showPhysicalRegistrationButton && showRemoteRegistrationButton;

  function renderButtons() {
    if (showBothButtons) {
      if (
        new Date(eventPage?.endDateForRegistration) <
        new Date(eventPage?.endDateForRegistrationRemote)
      ) {
        return (
          <>
            <RegistrationButton type="physical" eventPage={eventPage} />
            <RegistrationButton type="remote" eventPage={eventPage} />
          </>
        );
      } else {
        return (
          <>
            <RegistrationButton type="remote" eventPage={eventPage} />
            <RegistrationButton type="physical" eventPage={eventPage} />
          </>
        );
      }
    } else if (showPhysicalRegistrationButton && !showRemoteRegistrationButton) {
      return <RegistrationButton type="physical" eventPage={eventPage} />;
    } else if (!showPhysicalRegistrationButton && showRemoteRegistrationButton) {
      return <RegistrationButton type="remote" eventPage={eventPage} />;
    }
  }

  return (
    (showPhysicalRegistrationButton || showRemoteRegistrationButton) && (
      <div className={eventPageStyles.registrationWrapper}>{renderButtons()}</div>
    )
  );
};

const RegistrationText = (eventPage: ExtendedEventData) => {
  const remoteRegistrationDate = new Date(eventPage?.endDateForRegistrationRemote);
  const physicalRegistrationDate = new Date(eventPage?.endDateForRegistration);
  const remoteString = `Senast ${eventPage?.lastRegistrationDateRemoteString} för att delta digitalt.`;
  const physicalString = `Senast ${eventPage?.lastRegistrationDatePhysicalString} för att delta på plats.`;

  if (eventPage?.physicalRegistrationIsOpen && eventPage?.remoteRegistrationIsOpen) {
    if (physicalRegistrationDate < remoteRegistrationDate) {
      return (
        <>
          <li>
            <Text variant="span">{physicalString}</Text>
          </li>
          <li>
            <Text variant="span">{remoteString}</Text>
          </li>
        </>
      );
    } else {
      return (
        <>
          <li>
            <Text variant="span">{remoteString}</Text>
          </li>
          <li>
            <Text variant="span">{physicalString}</Text>
          </li>
        </>
      );
    }
  } else if (eventPage?.physicalRegistrationIsOpen) {
    return (
      <>
        <li>
          <Text variant="span">{physicalString}</Text>
        </li>
        {eventPage?.eventFormat === "Digitalt och på plats" && (
          <li>
            <Text variant="span">
              Det är inte längre möjligt att anmäla sig för att delta digitalt.
            </Text>
          </li>
        )}
      </>
    );
  } else if (eventPage?.remoteRegistrationIsOpen) {
    return (
      <>
        <li>
          <Text variant="span">{remoteString}</Text>
        </li>
        {eventPage?.eventFormat === "Digitalt och på plats" && (
          <li>
            <Text variant="span">
              Det är inte längre möjligt att anmäla sig för att delta på plats.
            </Text>
          </li>
        )}
      </>
    );
  }
};

export const RegistrationTextSection = (eventPage: ExtendedEventData) => {
  const registrationIsNotOpen =
    !eventPage?.physicalRegistrationIsOpen && !eventPage?.remoteRegistrationIsOpen;

  const isSameDate =
    eventPage?.lastRegistrationDatePhysicalString === eventPage?.lastRegistrationDateRemoteString;
  return (
    <>
      {registrationIsNotOpen ? (
        <Text variant="paragraph" removeMargin>
          Det går inte längre att anmäla sig till det här evenemanget.
        </Text>
      ) : isSameDate ? (
        <>
          <Text
            variant="paragraph"
            removeMargin
            data-contentful-entry-id={eventPage.sys.id}
            data-contentful-field-id="endDateForRegistration"
          >
            Senast {eventPage?.lastRegistrationDatePhysicalString} för att delta.
          </Text>
        </>
      ) : (
        <>
          <ul
            className={eventPageStyles.eventDatesUl}
            data-contentful-entry-id={eventPage.sys.id}
            data-contentful-field-id="endDateForRegistration"
          >
            {RegistrationText(eventPage)}
          </ul>
        </>
      )}
    </>
  );
};
